/**
 * Pure rules for payment sources ("Betalt med"). No database access.
 *
 * A payment source is the plain-language name for the motkonto: the bank
 * account, cash, the owner's own money, a supplier or customer credit, or the
 * tax account. Which BAS account the owner's money lands on differs by entity
 * type and direction, so that switch is exhaustive.
 */
import type { Company, EntityType, IsoDateTime, PaymentSource, PaymentSourceKind, Uuid } from '../types';

export type MoneyDirection = 'in' | 'out';

/**
 * Owner account by entity and direction.
 *   EF:            in 2018 (Egen insättning) / out 2013 (Eget uttag)
 *   AB:            in 2893 (Skuld till närstående) / out 1685 (Fordran på närstående)
 *   förening, BRF: 2893 both ways
 */
export function ownerAccountFor(entity: EntityType, direction: MoneyDirection): number {
  switch (entity) {
    case 'enskild_firma':
      return direction === 'in' ? 2018 : 2013;
    case 'aktiebolag':
      return direction === 'in' ? 2893 : 1685;
    case 'ideell_forening':
    case 'bostadsrattsforening':
      return 2893;
    default: {
      const exhaustive: never = entity;
      throw new Error(`Unknown entity type ${String(exhaustive)}`);
    }
  }
}

/** Default BAS account for a new payment source of the given kind. */
export function defaultAccountForKind(kind: PaymentSourceKind, entity: EntityType): number {
  switch (kind) {
    case 'bank':
    case 'card':
    case 'swish':
      return 1930;
    case 'cash':
      return 1910;
    case 'owner':
      return ownerAccountFor(entity, 'in');
    case 'supplier_credit':
      return 2440;
    case 'customer_credit':
      return 1510;
    case 'tax_account':
      return 1630;
    default: {
      const exhaustive: never = kind;
      throw new Error(`Unknown payment source kind ${String(exhaustive)}`);
    }
  }
}

/**
 * The account a payment source hits for a given money direction. Only the
 * owner source is direction-dependent; every other source has one account.
 */
export function paymentSourceAccount(source: Pick<PaymentSource, 'kind' | 'accountNumber'>, entity: EntityType, direction: MoneyDirection): number {
  return source.kind === 'owner' ? ownerAccountFor(entity, direction) : source.accountNumber;
}

/** BAS class a payment source account must belong to. */
export function accountClassForKind(kind: PaymentSourceKind): 1 | 2 {
  switch (kind) {
    case 'bank':
    case 'card':
    case 'swish':
    case 'cash':
    case 'customer_credit':
    case 'tax_account':
      return 1;
    case 'owner':
    case 'supplier_credit':
      return 2;
    default: {
      const exhaustive: never = kind;
      throw new Error(`Unknown payment source kind ${String(exhaustive)}`);
    }
  }
}

export const PAYMENT_SOURCE_KIND_LABELS: Record<PaymentSourceKind, string> = {
  bank: 'Bankkonto',
  card: 'Kort',
  swish: 'Swish',
  cash: 'Kontanter',
  owner: 'Egna pengar',
  supplier_credit: 'Leverantörsskuld',
  customer_credit: 'Kundfordran',
  tax_account: 'Skattekonto',
};

export const DEFAULT_PAYMENT_SOURCE_NAMES = {
  bank: 'Företagskontot',
  cash: 'Kontant',
  owner: 'Egna pengar',
} as const;

export type PaymentSourceRuleError =
  | { code: 'missing_name'; message: string }
  | { code: 'wrong_class'; message: string }
  | { code: 'missing_linked_bank'; message: string }
  | { code: 'duplicate_name'; message: string };

export type PaymentSourceRuleResult = { ok: true } | { ok: false; errors: PaymentSourceRuleError[] };

export interface PaymentSourceInput {
  name: string;
  kind: PaymentSourceKind;
  accountNumber: number;
  linkedBankSourceId?: Uuid;
}

export function validatePaymentSource(input: PaymentSourceInput, existingNames: ReadonlySet<string> = new Set()): PaymentSourceRuleResult {
  const errors: PaymentSourceRuleError[] = [];
  const name = input.name.trim();
  if (!name) errors.push({ code: 'missing_name', message: 'Betalkällan behöver ett namn' });
  if (existingNames.has(name.toLowerCase())) {
    errors.push({ code: 'duplicate_name', message: `Det finns redan en betalkälla som heter ${name}` });
  }
  const wanted = accountClassForKind(input.kind);
  if (!Number.isInteger(input.accountNumber) || Math.floor(input.accountNumber / 1000) !== wanted) {
    errors.push({ code: 'wrong_class', message: `${PAYMENT_SOURCE_KIND_LABELS[input.kind]} måste kopplas till ett konto i klass ${wanted}` });
  }
  if ((input.kind === 'card' || input.kind === 'swish') && !input.linkedBankSourceId) {
    errors.push({ code: 'missing_linked_bank', message: 'Kort och Swish behöver ett bankkonto som pengarna dras från' });
  }
  return errors.length ? { ok: false, errors } : { ok: true };
}

/**
 * The three payment sources every company starts with. Pure so the Dexie
 * upgrade step and `createCompany` build identical rows.
 */
export function defaultPaymentSources(company: Pick<Company, 'id' | 'entityType'>, now: IsoDateTime = new Date().toISOString()): PaymentSource[] {
  const make = (name: string, kind: PaymentSourceKind, accountNumber: number): PaymentSource => ({
    id: crypto.randomUUID(),
    companyId: company.id,
    name,
    kind,
    accountNumber,
    active: true,
    createdAt: now,
  });
  return [
    make(DEFAULT_PAYMENT_SOURCE_NAMES.bank, 'bank', 1930),
    make(DEFAULT_PAYMENT_SOURCE_NAMES.cash, 'cash', 1910),
    make(DEFAULT_PAYMENT_SOURCE_NAMES.owner, 'owner', ownerAccountFor(company.entityType, 'in')),
  ];
}
